import React, { useState, useRef, useEffect } from 'react';
import { Pencil1Icon, ArrowUpIcon} from '@radix-ui/react-icons';
import { HiOutlineSparkles } from 'react-icons/hi';
import { useTheme } from '../Theme/ThemeProvider';


interface SmartSuggestionProps {
  pmo: string;
  onSend: (pmo: string) => void;
}

const SmartSuggestion: React.FC<SmartSuggestionProps> = ({ pmo, onSend }) => {
  const { mode } = useTheme();
  const isLight = mode === 'light';
  const [editablePmo, setEditablePmo] = useState(pmo);
  const [editing, setEditing] = useState(false);
  const [outerHovered, setOuterHovered] = useState(false);
  const [innerHovered, setInnerHovered] = useState(false);
  const [invalid, setInvalid] = useState(false);
  const invalidTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Detection is PMO-only, but editing allows any valid Jira key (e.g. AIC-222)
  const isValidPMO = (value: string) => /^[A-Za-z]{1,10}-\d{1,}$/.test(value);

  const commitEdit = (value: string) => {
    setEditing(false);
    if (!isValidPMO(value)) {
      setInvalid(true);
      setEditablePmo('Invalid format');
      if (invalidTimerRef.current) clearTimeout(invalidTimerRef.current);
      invalidTimerRef.current = setTimeout(() => {
        setInvalid(false);
        setEditablePmo(pmo);
      }, 3000);
    }
  };

  useEffect(() => () => { if (invalidTimerRef.current) clearTimeout(invalidTimerRef.current); }, []);

  const handleEditClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setEditing(true);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditablePmo(e.target.value);
  };

  const handleInputBlur = () => {
    commitEdit(editablePmo);
  };

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      commitEdit(editablePmo);
    }
  };

  return (
    <div className="flex justify-end w-full">
      <div className="flex items-center gap-2">
        <span
          className="inline-flex items-center px-3 py-1 rounded-lg bg-[var(--color-secondary)] border border-[var(--color-accent)] text-sm font-medium shadow-sm cursor-pointer"
          style={{
            whiteSpace: 'nowrap',
            color: 'var(--color-text)',
            backgroundColor: outerHovered && !innerHovered ? 'var(--color-accent)' : undefined,
          }}
          aria-label={`Send ${editablePmo} as message`}
          onClick={() => onSend(editablePmo)}
          onMouseOver={() => setOuterHovered(true)}
          onMouseOut={() => setOuterHovered(false)}
        >
          {React.createElement(HiOutlineSparkles as React.ComponentType<any>, { size: 16, style: { display: 'inline', verticalAlign: 'middle', marginRight: 5, marginBottom: 2 } })}
          <span className={"px-1"} style={{ position: 'relative', top: '1px' }}>Click here to generate a Jira card for{' '}</span>
          {editing ? (

            <input
              type="text"
              value={editablePmo}
              onChange={handleInputChange}
              onBlur={handleInputBlur}
              onKeyDown={handleInputKeyDown}
              className="inline-block w-24 px-1 py-0.5 rounded border border-[var(--color-accent)] text-[var(--color-text)] text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)]"
              style={{ marginRight: 4, background: 'var(--main-background)' }}
              autoFocus
            />
          ) : (
            <span
              className="inline-flex items-center px-1 py-0.5 rounded border font-mono cursor-pointer"
              style={{
                marginRight: 4,
                background: invalid ? '#fee2e2' : innerHovered
                  ? (isLight ? '#9ca3af' : '#4b5563')
                  : (isLight ? '#d1d5db' : 'var(--main-background)'),
                opacity: 1,
                borderColor: invalid ? '#ef4444' : 'transparent',
                color: invalid ? '#dc2626' : undefined,
                transition: 'background 0.2s, border-color 0.2s, color 0.2s',
              }}
              onClick={handleEditClick}
              tabIndex={0}
              role="button"
              aria-label="Edit PMO code"
              title="Edit"
              onMouseOver={e => { e.stopPropagation(); setInnerHovered(true); setOuterHovered(false); }}
              onMouseOut={e => { e.stopPropagation(); setInnerHovered(false); }}
              onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { handleEditClick(e as any); } }}
            >
              {editablePmo}
              <Pencil1Icon style={{ marginLeft: 6 }} />
            </span>
          )}
          <ArrowUpIcon style={{ marginLeft: 10, flexShrink: 0 }} />
        </span>
      </div>
    </div>
  );
};

export default SmartSuggestion;
